import { Component, computed, inject, input, Signal } from '@angular/core';
import { ReleaseObjectGroupViewModel } from '../../models/autogenerated-piping';
import { ReleaseLifecycleService } from '../../services/release-lifecycle.service';

/**
 * Notice for pipe class / valve editors in an unpublished release:
 * a warning when another tab changed the life cycle after this page loaded, otherwise the reason
 * the editor is read-only (retire, or release as is in a non-draft release).
 */
@Component({
  selector: 'app-release-lifecycle-notice',
  templateUrl: './release-lifecycle-notice.component.html',
  styleUrls: ['./release-lifecycle-notice.component.scss'],
  standalone: false
})
export class ReleaseLifecycleNoticeComponent {
  private readonly lifecycleService: ReleaseLifecycleService = inject(ReleaseLifecycleService);

  /** The release object group this page was loaded with. */
  objectGroup = input<ReleaseObjectGroupViewModel | undefined | null>(undefined);

  /** Which editor hosts the notice; decides the URL the reload button opens. */
  objectType = input.required<'Pipe' | 'Valve'>();

  readonly isOutdated: Signal<boolean> = computed(() => this.lifecycleService.isOutdated(this.objectGroup()));
  readonly newLabel: Signal<string> = computed(() =>
    this.lifecycleService.getLabel(this.lifecycleService.getLatestChange(this.objectGroup())?.action));
  readonly lockReason: Signal<string | null> = computed(() => this.lifecycleService.getEditLockReason(this.objectGroup()));

  /** Opens the object the release row now points to; its id can differ from this page's after a life cycle change. */
  reload(): void {
    const objectId: string | undefined =
      this.lifecycleService.getLatestChange(this.objectGroup())?.newObjectId || this.objectGroup()?.newObjectId;
    if (!objectId) {
      window.location.reload();
      return;
    }
    window.location.assign(this.lifecycleService.getEditorUrl(this.objectType(), objectId));
  }
}
